import React, { useReducer, useCallback, useMemo, useContext } from 'react'
import reducer, {
  setViews,
  push,
  replace,
  pop,
  set,
  removeViewById,
  filter,
} from './reducer'

type StateContext = any[]

type ActionsContext = {
  setViews: (views: any[]) => void
  push: (component: React.ReactNode, options?: any) => void
  replace: (id: any, component: React.ReactNode, options?: any) => void
  pop: () => void
  close: () => void
  set: (id: any, options: any) => void
  remove: (id: any) => void
  filter: (filterFunction: (view: any) => Boolean) => void
}

const defaultActionsContextValue = {
  setViews: () => {},
  push: () => {},
  replace: () => {},
  pop: () => {},
  close: () => {},
  set: () => {},
  remove: () => {},
  filter: () => {},
}

const initialState = []

const createNavigationController = () => {
  const stateContext = React.createContext<StateContext>(initialState)
  const actionsContext = React.createContext<ActionsContext>(defaultActionsContextValue)

  const Provider = ({
    children
  }) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const _setViews = useCallback((views: any[]) => {
      dispatch(setViews(views))
    }, [dispatch])
  
    const _push = useCallback((component: React.ReactNode, options?: any) => {
      dispatch(push(component, options))
    }, [dispatch])
  
    const _replace = useCallback((id: any, component: React.ReactNode, options?: any) => {
      dispatch(replace(
        id,
        component,
        options,
      ))
    }, [dispatch])
  
    const _pop = useCallback(() => {
      dispatch(pop())
    }, [dispatch])
  
    const _close = useCallback(() => {
      dispatch(setViews([]))
    }, [dispatch])
  
    const _set = useCallback((id: any, options: any) => {
      dispatch(set(
        id,
        options,
      ))
    }, [dispatch])

    const _remove = useCallback((id) => {
      dispatch(removeViewById(id))
    }, [dispatch])

    const _filter = useCallback((filterFunction) => {
      dispatch(filter(filterFunction))
    }, [dispatch])
  
    const actions = useMemo(() => ({
      setViews: _setViews,
      push: _push,
      replace: _replace,
      pop: _pop,
      close: _close,
      set: _set,
      remove: _remove,
      filter: _filter,
    }), [
      _setViews,
      _push,
      _replace,
      _pop,
      _close,
      _set,
      _remove,
      _filter,
    ])
  
    return (
      <stateContext.Provider value={state}>
        <actionsContext.Provider value={actions}>
          {children}
        </actionsContext.Provider>
      </stateContext.Provider>
    )
  }

  const useViews = () => useContext(stateContext)

  const useActions = () => useContext(actionsContext)

  return {
    Provider,
    ViewsConsumer: stateContext.Consumer,
    ActionsConsumer: actionsContext.Consumer,
    useViews,
    useActions,
  }
}

export default createNavigationController
